import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Buildings from './pages/Buildings';
import BuildingDetail from './pages/Buildings/Detail';
import BuildingForm from './pages/Buildings/Form';
import BuildingOrder from './pages/Buildings/Order';
import BuildingUnits from './pages/Buildings/Units';
import BuildingUnitDetail from './pages/Buildings/UnitDetail';
import EquipmentUnitDetail from './pages/Buildings/EquipmentUnitDetail';
import BuildingSsInstanceDetail from './pages/Buildings/SsInstanceDetail';
import BuildingFloorPlan from './pages/Buildings/FloorPlan';
import BuildingLevels from './pages/Buildings/Levels';
import BuildingMandateForm from './pages/Buildings/MandateForm';
import BuildingChecklists from './pages/Buildings/Checklists';
import BuildingComplianceSchedule from './pages/Buildings/ComplianceSchedule';
import BuildingPaperwork from './pages/Buildings/Paperwork';
import BuildingDataPackLog from './pages/Buildings/DataPackLog';
import AdminPaperworkOverview from './pages/Paperwork/Overview';
import AdminPaperworkWorking from './pages/Paperwork/Working';
import AdminPaperworkTaskDetail from './pages/Paperwork/TaskDetail';
import IssuedForms from './pages/Mail/IssuedForms';
import BuildingChiller from './pages/Buildings/Chiller';
import BuildingInspectionModule from './pages/Buildings/InspectionModule';
import Schedule from './pages/Schedule';
import ChillerDashboard from './pages/Chiller';
import WorkerDetail from './pages/Workers/Detail';
import WorkerForm from './pages/Workers/Form';
import People from './pages/People';
import AppShelf from './pages/People/AppShelf';
import Admins from './pages/Admins';
import AdminDetail from './pages/Admins/Detail';
import AdminForm from './pages/Admins/Form';
import Jobs from './pages/Jobs';
import JobForm from './pages/Jobs/Form';
import JobDetail from './pages/Jobs/Detail';
import PeriodReport from './pages/Jobs/PeriodReport';
import BuildingInspections from './pages/Jobs/BuildingInspections';
import Repairing from './pages/Repairing/index';
import RepairCase from './pages/Repairing/Case';
import Settings from './pages/Settings';
import DbSetting from './pages/Settings/DbSetting';
import DbStructure from './pages/Settings/DbStructure';
import ChecklistTemplates from './pages/Settings/ChecklistTemplates';
import InspectionModuleSettings from './pages/Settings/InspectionModule';
import MailSettings from './pages/Settings/MailSettings';
import Documents from './pages/Documents';
import Application from './pages/Application';
import ApplyBwof from './pages/Application/ApplyBwof';
import AmendSsSiteVisitPage from './pages/Application/AmendSsSiteVisitPage';
import AmendSsVisitRespond from './pages/AmendSsVisitRespond';
import ServiceNetwork from './pages/ServiceNetwork';
import OrganizationDetail from './pages/ServiceNetwork/OrganizationDetail';
import PartnerDetail from './pages/ServiceNetwork/PartnerDetail';
import PartnerRegister from './pages/ServiceNetwork/PartnerRegister';
import BuildingSsHistory from './pages/ServiceNetwork/BuildingSsHistory';
import SsDateHistory from './pages/ServiceNetwork/SsDateHistory';
import ExpertForm from './pages/Experts/Form';
import ExpertOption from './pages/ExpertOption';
import Layout from './components/Layout';
import SiteInspectionFeed from './pages/SiteInspectionFeed';
import SiteInspectionChecklistPreview from './pages/SiteInspectionChecklistPreview';
import AuthReady from './components/AuthReady';

/** Lazy: heavy page — must not crash the whole SPA on cold load / Ctrl+F5 */
const AmendComplianceSchedules = lazy(() => import('./pages/Application/AmendComplianceSchedules'));

function PrivateRoute({ children }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const token = useAuthStore((s) => s.token);
  const location = useLocation();
  if (!isAuthenticated || !token) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return children;
}

function App() {
  return (
    <AuthReady>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/expert-option/:token" element={<ExpertOption />} />
      <Route path="/amend-ss-visit-respond/:token" element={<AmendSsVisitRespond />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="buildings" element={<Buildings />} />
        <Route path="buildings/new" element={<BuildingForm />} />
        <Route path="buildings/:id/edit" element={<BuildingForm />} />
        <Route path="buildings/:id/order" element={<BuildingOrder />} />
        <Route path="buildings/:id/units/:unitId" element={<BuildingUnitDetail />} />
        <Route path="buildings/:id/equipment-units/:unitId" element={<EquipmentUnitDetail />} />
        <Route path="buildings/:id/units" element={<BuildingUnits />} />
        <Route path="buildings/:id/ss/:instanceId" element={<BuildingSsInstanceDetail />} />
        <Route path="buildings/:id/floor-plan" element={<BuildingFloorPlan />} />
        <Route path="buildings/:id/levels" element={<BuildingLevels />} />
        <Route path="buildings/:id/mandates/new" element={<BuildingMandateForm />} />
        <Route path="buildings/:id/mandates/:mandateId/edit" element={<BuildingMandateForm />} />
        <Route path="buildings/:id/checklists" element={<BuildingChecklists />} />
        <Route path="buildings/:id/paperwork" element={<BuildingPaperwork />} />
        <Route path="buildings/:id/paperwork/pack-logs/:logId" element={<BuildingDataPackLog />} />
        <Route path="paperwork" element={<AdminPaperworkOverview />} />
        <Route path="paperwork/working" element={<AdminPaperworkWorking />} />
        <Route path="paperwork/tasks/:id" element={<AdminPaperworkTaskDetail />} />
        <Route path="buildings/:id/chiller" element={<BuildingChiller />} />
        <Route path="buildings/:id/inspection/:moduleCode" element={<BuildingInspectionModule />} />
        <Route path="buildings/:id/documents" element={<Documents />} />
        <Route path="buildings/:id/compliance-schedule" element={<BuildingComplianceSchedule />} />
        <Route path="buildings/:id" element={<BuildingDetail />} />
        <Route path="chiller" element={<ChillerDashboard />} />
        <Route path="schedule" element={<Schedule />} />
        <Route path="jobs" element={<Jobs />} />
        <Route path="jobs/new" element={<JobForm />} />
        <Route path="jobs/building/:buildingId" element={<BuildingInspections />} />
        <Route path="jobs/:id/report/:term" element={<PeriodReport />} />
        <Route path="jobs/:id" element={<JobDetail />} />
        <Route path="mail" element={<IssuedForms />} />
        <Route path="mail/issued-forms" element={<IssuedForms />} />
        <Route path="site-inspection" element={<SiteInspectionFeed />} />
        <Route path="site-inspection-checklist" element={<SiteInspectionChecklistPreview />} />
        <Route path="repairing" element={<Repairing />} />
        <Route path="repairing/cases/:caseId" element={<RepairCase />} />
        <Route path="workers" element={<Navigate to="/people" replace />} />
        <Route path="workers/new" element={<WorkerForm />} />
        <Route path="workers/:id/edit" element={<WorkerForm />} />
        <Route path="workers/:id" element={<WorkerDetail />} />
        <Route path="people" element={<People />} />
        <Route path="people/app-shelf" element={<AppShelf />} />
        <Route path="staff" element={<Navigate to="/people" replace />} />
        <Route path="admins" element={<Admins />} />
        <Route path="admins/new" element={<AdminForm />} />
        <Route path="admins/:id/edit" element={<AdminForm />} />
        <Route path="admins/:id" element={<AdminDetail />} />
        <Route path="application" element={<Application />} />
        <Route path="application/apply-bwof" element={<ApplyBwof />} />
        <Route
          path="application/amend-compliance-schedules"
          element={
            <Suspense fallback={<div className="p-6 text-sm text-gray-500">Loading…</div>}>
              <AmendComplianceSchedules />
            </Suspense>
          }
        />
        <Route path="application/amend-ss-site-visit" element={<AmendSsSiteVisitPage />} />
        <Route path="network" element={<ServiceNetwork />} />
        <Route path="network/partners/new" element={<PartnerRegister />} />
        <Route path="network/partners/:partnerId" element={<PartnerDetail />} />
        <Route path="network/org/:orgId" element={<OrganizationDetail />} />
        <Route path="network/org/:orgId/buildings/:buildingId" element={<BuildingSsHistory />} />
        <Route
          path="network/org/:orgId/buildings/:buildingId/ss/:ssCode"
          element={<SsDateHistory />}
        />
        <Route path="iqp-register" element={<Navigate to="/network?tab=iqp" replace />} />
        <Route path="partners" element={<Navigate to="/network?tab=our-partner" replace />} />
        <Route path="agent" element={<Navigate to="/network?tab=agent" replace />} />
        <Route path="agent/buildings" element={<Navigate to="/network?tab=agent" replace />} />
        <Route path="experts" element={<Navigate to="/network?tab=engineers" replace />} />
        <Route path="experts/new" element={<ExpertForm />} />
        <Route path="experts/:id/edit" element={<ExpertForm />} />
        <Route path="agent/ss-only" element={<Navigate to="/buildings?scope=ss_only" replace />} />
        <Route path="documents" element={<Documents />} />
        <Route path="settings" element={<Settings />} />
        <Route path="settings/mail" element={<MailSettings />} />
        <Route path="settings/db" element={<DbSetting />} />
        <Route path="settings/db/structure/:tableName" element={<DbStructure />} />
        <Route path="settings/checklists" element={<ChecklistTemplates />} />
        <Route path="settings/inspection" element={<InspectionModuleSettings />} />
      </Route>
    </Routes>
    </AuthReady>
  );
}

export default App;
